import * as React from 'react';
import { connect } from 'react-redux';

import SideNavigation from 'components/side-navigation-v2/SideNavigation';
import Scroller from './Scroller';

import { __t, applyTranslations } from 'utils/language';
import {
  getAgencyIdForOrganization,
  getAgencySettingsSubnavigation,
  getSettingsNavigation,
  getUserSettingsSubnavigation,
  mapNavigationNavItemToSideNavigationItem
} from 'utils/navigation';
import { filterAgencies, isCcmOnlyFocusedOrganization, isInfluencersFocusedOrganization } from 'utils/organization';
import { getSortedOrganizationsWithEntities } from 'utils/view';

import type { Organization } from 'types';
import type { SideNavigationContainer } from 'components/side-navigation-v2/SideNavigation.type';

applyTranslations(require('./settings-sidebar.i18n.json'));

type StateProps = {
  currentOrganization: Organization | undefined | null,
  organizations: Array<Organization>
};

type OwnProps = {};
type Props = StateProps & OwnProps;

class SettingsSidebar extends React.PureComponent<Props> {
  private getBrandsSettings(): Array<SideNavigationContainer> {
    const { organizations = [] } = this.props;
    const filteredOrganizations = filterAgencies(organizations)
      .filter(organization => !isInfluencersFocusedOrganization(organization));
    const sortedOrganization = getSortedOrganizationsWithEntities(filteredOrganizations);

    const brandsSettings = sortedOrganization
      .reduce<Array<SideNavigationContainer>>((result, organization) => {
        const settings = getSettingsNavigation(organization);
        const entity = organization.entity;

        if (settings.length) {
          result.push({
            type: 'container',
            title: entity.displayName ?? '',
            children: settings.map(mapNavigationNavItemToSideNavigationItem)
          });
        }

        return result;
      }, []);

    if (!brandsSettings.length) {
      return [];
    }

    return [{
      type: 'container',
      title: __t('settings_sidebar.brand_settings'),
      children: brandsSettings
    }];
  }

  private getCreatorSettings = (): Array<SideNavigationContainer> => [{
    type: 'container',
    title: __t('settings_sidebar.influencer_settings'),
    children: getSettingsNavigation(this.props.currentOrganization)
      .map(mapNavigationNavItemToSideNavigationItem)
  }];

  private getOrganizationSettings = (isInfluencerOrganization: boolean) => { // eslint-disable-line arrow-body-style
    return isInfluencerOrganization ? this.getCreatorSettings() : this.getBrandsSettings();
  };

  render() {
    const { currentOrganization } = this.props;

    const userSettings = getUserSettingsSubnavigation(currentOrganization);
    const agencyOrganizationId = currentOrganization && getAgencyIdForOrganization(currentOrganization);
    const isInfluencerOrganization = isInfluencersFocusedOrganization(currentOrganization);
    const isCcmOnlyOrganization = isCcmOnlyFocusedOrganization(currentOrganization);

    let items: Array<SideNavigationContainer> = [
      {
        type: 'container',
        title: __t('settings_sidebar.account_settings'),
        children: userSettings.map(mapNavigationNavItemToSideNavigationItem)
      }
    ];

    if (currentOrganization && agencyOrganizationId && !isInfluencerOrganization && !isCcmOnlyOrganization) {
      items.push({
        type: 'container',
        title: __t('settings_sidebar.agency_settings'),
        children: getAgencySettingsSubnavigation(agencyOrganizationId)
          .map(mapNavigationNavItemToSideNavigationItem)
      });
    }

    if (currentOrganization && currentOrganization.type !== 'influencers_agency') {
      items = items.concat(this.getOrganizationSettings(isInfluencerOrganization));
    }

    return (
      <div className="settings-sidebar">
        <Scroller
          scrollableElementSelector={'[data-for-scroller="side-page-wrapper"]'}
          elementToScrollSelector=".side-navigation__item--active"
        />
        <SideNavigation
          items={items}
        />
      </div>
    );
  }
}

const mapStateToProps = ({ organizations }): StateProps => ({
  organizations: organizations.list,
  currentOrganization: organizations.currentOrganization
});

export default connect(mapStateToProps)(SettingsSidebar);
